// Pantalla del rol nutricionista (Sprint 5 T11). Tabs: Ingredientes / Platos /
// Dietas / Menus. Cada tab maneja workflow ADR-007 + Δ3 (auto-aprobacion
// cuando solo hay 1 nutricionista activa).
import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/Toast'
import { useNutricionistaCatalogoStore, type TabKey } from '../stores/nutricionistaCatalogo'
import WorkflowBadge from '../components/nutricionista/WorkflowBadge'
import IngredienteForm from '../components/nutricionista/IngredienteForm'
import PlatoTemplateForm from '../components/nutricionista/PlatoTemplateForm'
import DietaCard from '../components/nutricionista/DietaCard'
import MenuCard from '../components/nutricionista/MenuCard'
import AsignarPlatosModal from '../components/nutricionista/AsignarPlatosModal'
import CalendarioMenuView from '../components/nutricionista/CalendarioMenuView'
import PlatoBomViewer from '../components/nutricionista/PlatoBomViewer'
import ConfirmarAutoAprobacion from '../components/nutricionista/ConfirmarAutoAprobacion'
import BloquearModal from '../components/nutricionista/BloquearModal'
import DesbloquearModal from '../components/nutricionista/DesbloquearModal'
import NuevoMenuModal from '../components/nutricionista/NuevoMenuModal'
import NuevaDietaModal from '../components/nutricionista/NuevaDietaModal'
import type {
  IngredienteCreateBody,
  IngredienteResponse,
  IngredienteUpdateBody,
  MenuResponse,
  PlatoTemplateCreateBody,
  PlatoTemplateResponse,
  PlatoTemplateUpdateBody,
} from '../services/nutricionista'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'ingredientes', label: 'Ingredientes' },
  { key: 'platos', label: 'Platos' },
  { key: 'dietas', label: 'Dietas' },
  { key: 'menus', label: 'Menús' },
]

type TargetWorkflow =
  | { kind: 'ingrediente'; item: IngredienteResponse }
  | { kind: 'plato'; item: PlatoTemplateResponse }

export default function NutricionistaCatalogo() {
  const { user, logout } = useAuth()
  const { showToast } = useToast()
  const store = useNutricionistaCatalogoStore()
  const {
    ingredientes,
    platos,
    dietas,
    menus,
    loading,
    error,
    tabActivo,
    ultimaTransicion,
    setTabActivo,
    refrescarTodo,
    crearIngrediente,
    actualizarIngrediente,
    enviarAprobacionIngrediente,
    autoAprobarIngrediente,
    bloquearIngrediente,
    desbloquearIngrediente,
    crearPlato,
    actualizarPlato,
    enviarAprobacionPlato,
    autoAprobarPlato,
    bloquearPlato,
    desbloquearPlato,
    crearDieta,
    actualizarDieta,
    crearMenu,
    asignarPlatosMenu,
    activarMenu,
    archivarMenu,
    limpiarUltimaTransicion,
  } = store

  const [filtroCategoria, setFiltroCategoria] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [editIngrediente, setEditIngrediente] = useState<IngredienteResponse | null>(null)
  const [crearIngOpen, setCrearIngOpen] = useState(false)
  const [editPlato, setEditPlato] = useState<PlatoTemplateResponse | null>(null)
  const [crearPlatoOpen, setCrearPlatoOpen] = useState(false)
  const [autoAprobarTarget, setAutoAprobarTarget] = useState<TargetWorkflow | null>(null)
  const [bloquearTarget, setBloquearTarget] = useState<TargetWorkflow | null>(null)
  const [desbloquearTarget, setDesbloquearTarget] = useState<TargetWorkflow | null>(null)
  const [menuAsignar, setMenuAsignar] = useState<MenuResponse | null>(null)
  const [menuCalendario, setMenuCalendario] = useState<MenuResponse | null>(null)
  const [verBomPlato, setVerBomPlato] = useState<PlatoTemplateResponse | null>(null)
  const [showNuevoMenu, setShowNuevoMenu] = useState(false)
  const [showNuevaDieta, setShowNuevaDieta] = useState(false)

  useEffect(() => {
    refrescarTodo()
  }, [refrescarTodo])

  useEffect(() => {
    if (error) showToast(error, 'error')
  }, [error, showToast])

  useEffect(() => {
    if (ultimaTransicion) {
      const accion = ultimaTransicion.auto_aprobado
        ? 'AUTO-APROBADO'
        : ultimaTransicion.estado_nuevo
      showToast(
        `${ultimaTransicion.entidad_tipo} #${ultimaTransicion.entidad_id} → ${accion}`,
        'success',
      )
      limpiarUltimaTransicion()
    }
  }, [ultimaTransicion, showToast, limpiarUltimaTransicion])

  const ingredientesFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    return ingredientes.filter((i) => {
      if (filtroCategoria && (i.categoria ?? '') !== filtroCategoria) return false
      if (!q) return true
      return (
        i.codigo.toLowerCase().includes(q) ||
        i.nombre.toLowerCase().includes(q)
      )
    })
  }, [ingredientes, busqueda, filtroCategoria])

  const platosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    return platos.filter((p) => {
      if (!q) return true
      return (
        p.codigo.toLowerCase().includes(q) ||
        p.nombre_compuesto.toLowerCase().includes(q)
      )
    })
  }, [platos, busqueda])

  const dietasFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    if (!q) return dietas
    return dietas.filter(
      (d) => d.codigo.toLowerCase().includes(q) || d.nombre.toLowerCase().includes(q),
    )
  }, [dietas, busqueda])

  const menusFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    if (!q) return menus
    return menus.filter(
      (m) => m.codigo.toLowerCase().includes(q) || m.nombre.toLowerCase().includes(q),
    )
  }, [menus, busqueda])

  const categoriasIngrediente = useMemo(() => {
    const set = new Set<string>()
    for (const i of ingredientes) if (i.categoria) set.add(i.categoria)
    return Array.from(set).sort()
  }, [ingredientes])

  // ----- Handlers de workflow -----
  async function handleEnviarAprobacionIng(it: IngredienteResponse) {
    try {
      await enviarAprobacionIngrediente(it.id)
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'No se pudo enviar', 'error')
    }
  }

  async function handleEnviarAprobacionPlato(it: PlatoTemplateResponse) {
    try {
      await enviarAprobacionPlato(it.id)
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'No se pudo enviar', 'error')
    }
  }

  async function handleAutoAprobarConfirm(justif: string) {
    if (!autoAprobarTarget) return
    try {
      if (autoAprobarTarget.kind === 'ingrediente') {
        await autoAprobarIngrediente(autoAprobarTarget.item.id, { justificacion: justif })
      } else {
        await autoAprobarPlato(autoAprobarTarget.item.id, { justificacion: justif })
      }
      setAutoAprobarTarget(null)
    } catch (err) {
      throw err
    }
  }

  async function handleBloquearConfirm(motivo: string) {
    if (!bloquearTarget) return
    try {
      if (bloquearTarget.kind === 'ingrediente') {
        await bloquearIngrediente(bloquearTarget.item.id, { motivo })
      } else {
        await bloquearPlato(bloquearTarget.item.id, { motivo })
      }
      setBloquearTarget(null)
    } catch (err) {
      throw err
    }
  }

  async function handleDesbloquearConfirm(motivo: string) {
    if (!desbloquearTarget) return
    try {
      if (desbloquearTarget.kind === 'ingrediente') {
        await desbloquearIngrediente(desbloquearTarget.item.id, { motivo })
      } else {
        await desbloquearPlato(desbloquearTarget.item.id, { motivo })
      }
      setDesbloquearTarget(null)
    } catch (err) {
      throw err
    }
  }

  return (
    <div className="min-h-screen">
      <header
        className="sticky top-0 z-30 px-4 md:px-6 py-3 flex items-center gap-3"
        style={{
          background: 'linear-gradient(120deg,#143C78,#1E1E3C)',
          borderBottom: '1px solid rgba(140,170,230,.18)',
        }}
      >
        <img
          src="/avante-logo-white.png"
          alt="Avante"
          style={{ height: 30 }}
          onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
        />
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold leading-tight truncate" style={{ fontFamily: 'Space Grotesk' }}>
            Catálogo de nutrición
          </h1>
          <p className="text-xs text-[#94A0CD] truncate">{user?.nombre_completo}</p>
        </div>
        <button onClick={logout} className="text-sm text-[#94A0CD] hover:text-white shrink-0">
          Salir
        </button>
      </header>

      <div
        role="tablist"
        aria-label="Pestañas de catálogo"
        className="flex gap-1 px-4 pt-2 sticky top-[58px] z-20 overflow-x-auto"
        style={{ background: '#1c1c38', borderBottom: '1px solid rgba(140,170,230,.18)' }}
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tabActivo === t.key}
            aria-controls={`panel-${t.key}`}
            id={`tab-${t.key}`}
            onClick={() => { setTabActivo(t.key); setBusqueda(''); setFiltroCategoria('') }}
            className="px-4 py-2.5 text-sm font-semibold rounded-t-xl whitespace-nowrap"
            style={
              tabActivo === t.key
                ? {
                    background: 'linear-gradient(160deg, rgba(0,110,180,.30), rgba(0,110,180,.12))',
                    color: '#7FD4FF',
                    border: '1px solid rgba(0,110,180,.5)',
                    borderBottom: 'none',
                    fontFamily: 'Space Grotesk',
                  }
                : { color: '#94A0CD', fontFamily: 'Space Grotesk' }
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      <main className="p-4 space-y-4 mx-auto" style={{ maxWidth: 1150 }}>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="block flex-1 min-w-[200px]">
            <span className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: '#8B97C8' }}>Buscar</span>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Código o nombre…"
              aria-label="Buscar"
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
          </label>
          {tabActivo === 'ingredientes' && (
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: '#8B97C8' }}>Categoría</span>
              <select
                value={filtroCategoria}
                onChange={(e) => setFiltroCategoria(e.target.value)}
                aria-label="Filtrar por categoria"
                className="mt-1 border rounded px-3 py-2 text-sm"
              >
                <option value="">Todas</option>
                {categoriasIngrediente.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
          )}
          {tabActivo === 'ingredientes' && (
            <button
              type="button"
              onClick={() => setCrearIngOpen(true)}
              className="px-4 text-white rounded text-sm font-semibold"
              style={{ minHeight: 44, borderRadius: 14, background: 'linear-gradient(120deg,#006EB4,#143C78)', boxShadow: '0 10px 22px -8px rgba(0,110,180,.7)' }}
            >
              + Nuevo ingrediente
            </button>
          )}
          {tabActivo === 'platos' && (
            <button
              type="button"
              onClick={() => setCrearPlatoOpen(true)}
              className="px-4 text-white rounded text-sm font-semibold"
              style={{ minHeight: 44, borderRadius: 14, background: 'linear-gradient(120deg,#006EB4,#143C78)', boxShadow: '0 10px 22px -8px rgba(0,110,180,.7)' }}
            >
              + Nuevo plato
            </button>
          )}
        </div>

        {loading && <p className="text-sm text-gray-500">Cargando…</p>}
        {error && (
          <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </p>
        )}

        {tabActivo === 'ingredientes' && (
          <section
            id="panel-ingredientes"
            role="tabpanel"
            aria-labelledby="tab-ingredientes"
          >
            {ingredientesFiltrados.length === 0 && !loading && (
              <p className="text-sm text-gray-500">Sin ingredientes.</p>
            )}
            <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded shadow">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100 dark:bg-gray-700 text-left">
                  <tr>
                    <th className="p-2">Codigo</th>
                    <th className="p-2">Nombre</th>
                    <th className="p-2">Categoria</th>
                    <th className="p-2">Estado</th>
                    <th className="p-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {ingredientesFiltrados.map((it) => {
                    const estado = String(it.estado_workflow).toUpperCase()
                    return (
                      <tr key={it.id} className="border-t">
                        <td className="p-2 font-mono text-xs">{it.codigo}</td>
                        <td className="p-2">{it.nombre}</td>
                        <td className="p-2 text-xs">{it.categoria ?? '—'}</td>
                        <td className="p-2"><WorkflowBadge estado={estado} /></td>
                        <td className="p-2 flex gap-1 flex-wrap">
                          {estado === 'BORRADOR' && (
                            <>
                              <button
                                type="button"
                                onClick={() => setEditIngrediente(it)}
                                className="text-xs px-2 py-1 border rounded"
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEnviarAprobacionIng(it)}
                                className="text-xs px-2 py-1 bg-orange-500 text-white rounded"
                              >
                                Enviar a aprobacion
                              </button>
                            </>
                          )}
                          {estado === 'PENDIENTE' && (
                            <button
                              type="button"
                              onClick={() =>
                                setAutoAprobarTarget({ kind: 'ingrediente', item: it })
                              }
                              className="text-xs px-2 py-1 bg-yellow-600 text-white rounded"
                            >
                              Auto-aprobar
                            </button>
                          )}
                          {estado === 'APROBADO' && (
                            <>
                              <button
                                type="button"
                                onClick={() => setEditIngrediente(it)}
                                className="text-xs px-2 py-1 border rounded"
                                title="Editar permitido en APROBADO con auditoria"
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setBloquearTarget({ kind: 'ingrediente', item: it })
                                }
                                className="text-xs px-2 py-1 bg-avante-danger text-white rounded"
                              >
                                Bloquear
                              </button>
                            </>
                          )}
                          {estado === 'BLOQUEADO' && (
                            <button
                              type="button"
                              onClick={() =>
                                setDesbloquearTarget({ kind: 'ingrediente', item: it })
                              }
                              className="text-xs px-2 py-1 bg-avante-success text-white rounded"
                            >
                              Desbloquear
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tabActivo === 'platos' && (
          <section
            id="panel-platos"
            role="tabpanel"
            aria-labelledby="tab-platos"
          >
            {platosFiltrados.length === 0 && !loading && (
              <div className="bg-white rounded-lg shadow p-6 text-sm max-w-2xl">
                <p className="font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>
                  Recetario formal vacío (esto es normal)
                </p>
                <p className="text-gray-500">
                  Este módulo es para fichas técnicas versionadas con flujo de
                  aprobación (Borrador → Aprobado). Los platos que se sirven hoy
                  viven en el tab <b>Menús</b>, y sus recetas operativas
                  (gramajes e ingredientes) ya están cargadas en el motor de
                  inventario. Usa «+ Nuevo plato» cuando quieras formalizar una
                  ficha técnica aquí.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {platosFiltrados.map((p) => {
                const estado = String(p.estado).toUpperCase()
                return (
                  <article
                    key={p.id}
                    className="bg-white dark:bg-gray-800 rounded shadow p-3"
                    aria-labelledby={`plato-${p.id}-title`}
                  >
                    <header className="flex justify-between items-start mb-2">
                      <h3 id={`plato-${p.id}-title`} className="font-bold text-sm">
                        {p.codigo}
                      </h3>
                      <WorkflowBadge estado={estado} />
                    </header>
                    <p className="text-sm">{p.nombre_compuesto}</p>
                    <p className="text-xs text-gray-500 mt-1">v{p.version}</p>
                    <footer className="flex gap-1 mt-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setVerBomPlato(p)}
                        className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                        title="Ver receta (ingredientes y cantidades)"
                      >
                        Ver receta
                      </button>
                      {estado === 'BORRADOR' && (
                        <>
                          <button
                            type="button"
                            onClick={() => setEditPlato(p)}
                            className="text-xs px-2 py-1 border rounded"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEnviarAprobacionPlato(p)}
                            className="text-xs px-2 py-1 bg-orange-500 text-white rounded"
                          >
                            Enviar
                          </button>
                        </>
                      )}
                      {(estado === 'PENDIENTE' || estado === 'EN_REVISION') && (
                        <button
                          type="button"
                          onClick={() =>
                            setAutoAprobarTarget({ kind: 'plato', item: p })
                          }
                          className="text-xs px-2 py-1 bg-yellow-600 text-white rounded"
                        >
                          Auto-aprobar
                        </button>
                      )}
                      {estado === 'APROBADO' && (
                        <button
                          type="button"
                          onClick={() =>
                            setBloquearTarget({ kind: 'plato', item: p })
                          }
                          className="text-xs px-2 py-1 bg-avante-danger text-white rounded"
                        >
                          Bloquear
                        </button>
                      )}
                      {estado === 'BLOQUEADO' && (
                        <button
                          type="button"
                          onClick={() =>
                            setDesbloquearTarget({ kind: 'plato', item: p })
                          }
                          className="text-xs px-2 py-1 bg-avante-success text-white rounded"
                        >
                          Desbloquear
                        </button>
                      )}
                    </footer>
                  </article>
                )
              })}
            </div>
          </section>
        )}

        {tabActivo === 'dietas' && (
          <section
            id="panel-dietas"
            role="tabpanel"
            aria-labelledby="tab-dietas"
          >
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                onClick={() => setShowNuevaDieta(true)}
                className="bg-avante-primary text-white px-4 py-2 rounded font-medium hover:bg-avante-accent"
              >
                + Nueva dieta
              </button>
            </div>
            {dietasFiltradas.length === 0 && !loading && (
              <p className="text-sm text-gray-500">Sin dietas.</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {dietasFiltradas.map((d) => (
                <DietaCard
                  key={d.id}
                  dieta={d}
                  onSave={async (body) => {
                    await actualizarDieta(d.id, body)
                  }}
                />
              ))}
            </div>
          </section>
        )}

        {tabActivo === 'menus' && (
          <section
            id="panel-menus"
            role="tabpanel"
            aria-labelledby="tab-menus"
          >
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                onClick={() => setShowNuevoMenu(true)}
                className="bg-avante-primary text-white px-4 py-2 rounded font-medium hover:bg-avante-accent"
              >
                + Nuevo menú
              </button>
            </div>
            {menusFiltrados.length === 0 && !loading && (
              <p className="text-sm text-gray-500">Sin menús.</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {menusFiltrados.map((m) => (
                <MenuCard
                  key={m.id}
                  menu={m}
                  onAsignarPlatos={(menu) => setMenuAsignar(menu)}
                  onVerCalendario={(menu) => setMenuCalendario(menu)}
                  onActivar={async (menu) => {
                    try {
                      await activarMenu(menu.id)
                      showToast(`Menu ${menu.codigo} activado`, 'success')
                    } catch (err) {
                      showToast(err instanceof Error ? err.message : 'Error', 'error')
                    }
                  }}
                  onArchivar={async (menu) => {
                    try {
                      await archivarMenu(menu.id)
                      showToast(`Menu ${menu.codigo} archivado`, 'success')
                    } catch (err) {
                      showToast(err instanceof Error ? err.message : 'Error', 'error')
                    }
                  }}
                />
              ))}
            </div>
          </section>
        )}
      </main>

      {/* ---------- Modales ---------- */}
      {crearIngOpen && (
        <IngredienteForm
          onSubmit={async (body) => {
            await crearIngrediente(body as IngredienteCreateBody)
            setCrearIngOpen(false)
            showToast('Ingrediente creado en BORRADOR', 'success')
          }}
          onCancel={() => setCrearIngOpen(false)}
        />
      )}
      {editIngrediente && (
        <IngredienteForm
          initial={editIngrediente}
          onSubmit={async (body) => {
            await actualizarIngrediente(editIngrediente.id, body as IngredienteUpdateBody)
            setEditIngrediente(null)
            showToast('Cambios guardados', 'success')
          }}
          onCancel={() => setEditIngrediente(null)}
        />
      )}
      {crearPlatoOpen && (
        <PlatoTemplateForm
          ingredientesDisponibles={ingredientes}
          onSubmit={async (body) => {
            await crearPlato(body as PlatoTemplateCreateBody)
            setCrearPlatoOpen(false)
            showToast('Plato creado en BORRADOR', 'success')
          }}
          onCancel={() => setCrearPlatoOpen(false)}
        />
      )}
      {editPlato && (
        <PlatoTemplateForm
          initial={editPlato}
          ingredientesDisponibles={ingredientes}
          onSubmit={async (body) => {
            await actualizarPlato(editPlato.id, body as PlatoTemplateUpdateBody)
            setEditPlato(null)
            showToast('Cambios guardados', 'success')
          }}
          onCancel={() => setEditPlato(null)}
        />
      )}
      {autoAprobarTarget && (
        <ConfirmarAutoAprobacion
          entidadLabel={
            autoAprobarTarget.kind === 'ingrediente'
              ? `Ingrediente ${autoAprobarTarget.item.codigo} — ${autoAprobarTarget.item.nombre}`
              : `Plato ${autoAprobarTarget.item.codigo} — ${autoAprobarTarget.item.nombre_compuesto}`
          }
          onConfirm={handleAutoAprobarConfirm}
          onCancel={() => setAutoAprobarTarget(null)}
        />
      )}
      {bloquearTarget && (
        <BloquearModal
          entidadLabel={
            bloquearTarget.kind === 'ingrediente'
              ? `Ingrediente ${bloquearTarget.item.codigo} — ${bloquearTarget.item.nombre}`
              : `Plato ${bloquearTarget.item.codigo} — ${bloquearTarget.item.nombre_compuesto}`
          }
          onConfirm={handleBloquearConfirm}
          onCancel={() => setBloquearTarget(null)}
        />
      )}
      {desbloquearTarget && (
        <DesbloquearModal
          entidadLabel={
            desbloquearTarget.kind === 'ingrediente'
              ? `Ingrediente ${desbloquearTarget.item.codigo} — ${desbloquearTarget.item.nombre}`
              : `Plato ${desbloquearTarget.item.codigo} — ${desbloquearTarget.item.nombre_compuesto}`
          }
          onConfirm={handleDesbloquearConfirm}
          onCancel={() => setDesbloquearTarget(null)}
        />
      )}
      {menuAsignar && (
        <AsignarPlatosModal
          menu={menuAsignar}
          dietas={dietas}
          platos={platos}
          onConfirm={async (items) => {
            try {
              const res = await asignarPlatosMenu(menuAsignar.id, { items })
              showToast(`${res.insertados} celdas asignadas`, 'success')
              setMenuAsignar(null)
            } catch (err) {
              throw err
            }
          }}
          onCancel={() => setMenuAsignar(null)}
        />
      )}
      {verBomPlato && (
        <PlatoBomViewer plato={verBomPlato} onClose={() => setVerBomPlato(null)} />
      )}
      {menuCalendario && (
        <CalendarioMenuView
          menu={menuCalendario}
          dietas={dietas}
          platos={platos}
          onConfirm={async (items) => {
            const res = await asignarPlatosMenu(menuCalendario.id, { items })
            showToast(`${res.insertados} celdas asignadas`, 'success')
          }}
          onCancel={() => setMenuCalendario(null)}
        />
      )}

      {showNuevoMenu && (
        <NuevoMenuModal
          onClose={() => setShowNuevoMenu(false)}
          onSubmit={async (body) => {
            try {
              const nuevo = await crearMenu(body)
              showToast(`Menú ${nuevo.codigo} creado`, 'success')
            } catch (err) {
              showToast(err instanceof Error ? err.message : 'Error al crear menú', 'error')
              throw err
            }
          }}
        />
      )}

      {showNuevaDieta && (
        <NuevaDietaModal
          onClose={() => setShowNuevaDieta(false)}
          onSubmit={async (body) => {
            try {
              const nueva = await crearDieta(body)
              showToast(`Dieta ${nueva.codigo} creada`, 'success')
            } catch (err) {
              showToast(err instanceof Error ? err.message : 'Error al crear dieta', 'error')
              throw err
            }
          }}
        />
      )}
    </div>
  )
}
